import { useState, useEffect, useRef } from 'react';

export default function ImageGallery({ images, productName }) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isZooming, setIsZooming] = useState(false);
    const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
    const [lensPosition, setLensPosition] = useState({ x: 0, y: 0 });
    const [isZoomOnLeft, setIsZoomOnLeft] = useState(false);
    const imageRef = useRef(null);
    const carouselRef = useRef(null);
    const zoomPreviewRef = useRef(null);

    const LENS_SIZE = 180; // Size of the zoom lens overlay
    const ZOOM_PREVIEW_SIZE = 450;
    const ZOOM_MARGIN = 40;

    // Reset to first image when images change (variant switch)
    useEffect(() => {
        setSelectedIndex(0);
    }, [images]);

    const handleMouseMove = (e) => {
        if (!imageRef.current) return;

        const rect = imageRef.current.getBoundingClientRect();

        // Calculate cursor position relative to image
        const cursorX = e.clientX - rect.left;
        const cursorY = e.clientY - rect.top;

        // Calculate lens position (centered on cursor)
        let lensX = cursorX - LENS_SIZE / 2;
        let lensY = cursorY - LENS_SIZE / 2;

        // Keep lens within bounds
        lensX = Math.max(0, Math.min(lensX, rect.width - LENS_SIZE));
        lensY = Math.max(0, Math.min(lensY, rect.height - LENS_SIZE));

        // Calculate the center of the lens area
        const lensCenterX = lensX + LENS_SIZE / 2;
        const lensCenterY = lensY + LENS_SIZE / 2;

        // Convert lens center to percentage for background positioning
        // For backgroundSize: 200%, the position formula is: (percentage * 2) - 50
        // This ensures the zoomed preview is synced with the lens position
        const xPercent = (lensCenterX / rect.width) * 100;
        const yPercent = (lensCenterY / rect.height) * 100;
        const x = xPercent * 2 - 50;
        const y = yPercent * 2 - 50;

        setZoomPosition({ x, y });
        setLensPosition({ x: lensX, y: lensY });
    };

    const handleMouseEnter = () => {
        setIsZooming(true);
        
        // Check if there's enough space on the right side
        if (imageRef.current) {
            const rect = imageRef.current.getBoundingClientRect();
            const spaceOnRight = window.innerWidth - (rect.left + rect.width);
            const requiredSpace = ZOOM_PREVIEW_SIZE + ZOOM_MARGIN;
            
            setIsZoomOnLeft(spaceOnRight < requiredSpace);
        }
    };

    const handleMouseLeave = () => {
        setIsZooming(false);
    };

    const scrollCarousel = (direction) => {
        if (!carouselRef.current) return;

        const scrollAmount = 120; // Width of one thumbnail + gap
        const newScrollPosition = carouselRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);

        carouselRef.current.scrollTo({
            left: newScrollPosition,
            behavior: 'smooth'
        });
    };

    if (!images || images.length === 0) {
        return (
            <div className="lg:sticky lg:top-[180px]">
                <div className="w-full aspect-square max-h-[520px] flex items-center justify-center">
                    <div className="text-center text-muted-foreground/60">
                        <i className="ri-image-line text-6xl block mb-2"></i>
                        <p className="text-sm">No images available</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        // While zooming, lift the whole gallery above the ProductInfo column so
        // the zoom preview overlays it instead of the color swatches (a later DOM
        // sibling) painting on top of the preview.
        <div className={`lg:sticky lg:top-[180px] ${isZooming ? 'relative z-50' : ''}`}>
            {/* Main image — full bleed on page background, no muted plate */}
            <div className="relative w-full">
                <div
                    className="relative w-full aspect-square max-h-[520px] overflow-hidden mb-3"
                    ref={imageRef}
                >
                    <img
                        src={images[selectedIndex]}
                        alt={`${productName} - View ${selectedIndex + 1}`}
                        className="w-full h-full object-contain cursor-crosshair"
                        onMouseMove={handleMouseMove}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    />

                    {/* Zoom Lens Indicator */}
                    {isZooming && (
                        <div
                            className="absolute border-2 border-primary bg-card/30 pointer-events-none hidden lg:block"
                            style={{
                                width: `${LENS_SIZE}px`,
                                height: `${LENS_SIZE}px`,
                                left: `${lensPosition.x}px`,
                                top: `${lensPosition.y}px`,
                            }}
                        />
                    )}
                </div>

                {/* Zoomed Preview (Overlay on Right or Left Side) */}
                {isZooming && (
                    <div
                        ref={zoomPreviewRef}
                        className="hidden lg:block absolute top-0 w-[450px] h-[450px] bg-background rounded-lg overflow-hidden shadow-2xl border border-border z-50"
                        style={{
                            [isZoomOnLeft ? 'right' : 'left']: '100%',
                            marginRight: isZoomOnLeft ? `${ZOOM_MARGIN}px` : 0,
                            marginLeft: isZoomOnLeft ? 0 : `${ZOOM_MARGIN}px`,
                        }}
                    >
                        <div
                            className="w-full h-full"
                            style={{
                                // Quote the URL so filenames with spaces/parentheses
                                // (e.g. "Apple charger.png") don't break the CSS url().
                                backgroundImage: `url("${images[selectedIndex]}")`,
                                backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                                backgroundSize: '200%',
                                backgroundRepeat: 'no-repeat',
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Thumbnail Images */}
            {images.length > 1 && (
                <div className="relative">
                    {/* Left Arrow */}
                    {images.length > 4 && (
                        <button
                            onClick={() => scrollCarousel('left')}
                            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-card/90 hover:bg-card shadow-md rounded-full p-2 transition-all"
                            aria-label="Scroll left"
                        >
                            <i className="ri-arrow-left-s-line text-xl text-foreground/80"></i>
                        </button>
                    )}

                    {/* Thumbnails Container */}
                    <div
                        ref={carouselRef}
                        className={`flex gap-2.5 ${
                            images.length > 4
                                ? 'overflow-x-auto scrollbar-hide px-8'
                                : 'grid grid-cols-4'
                        }`}
                    >
                        {images.map((image, index) => (
                            <button
                                key={index}
                                onClick={() => setSelectedIndex(index)}
                                className={`relative ${
                                    images.length > 4 ? 'w-20 flex-shrink-0' : 'w-full'
                                } h-20 overflow-hidden border-2 transition-all rounded-md ${
                                    selectedIndex === index
                                        ? 'border-primary'
                                        : 'border-transparent hover:border-border'
                                }`}
                            >
                                <img
                                    src={image}
                                    alt={`${productName} - Thumbnail ${index + 1}`}
                                    className="w-full h-full object-contain"
                                />
                            </button>
                        ))}
                    </div>

                    {/* Right Arrow */}
                    {images.length > 4 && (
                        <button
                            onClick={() => scrollCarousel('right')}
                            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-card/90 hover:bg-card shadow-md rounded-full p-2 transition-all"
                            aria-label="Scroll right"
                        >
                            <i className="ri-arrow-right-s-line text-xl text-foreground/80"></i>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
